import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CardContent, CardHeader, CardTitle, CardV2 } from '../../../components/CardV2/CardV2'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react'
import PaymentsAPI from '../../../api/PaymentsAPI'
import { toastService } from '../../../services/ToastService'
import { ButtonV2 } from '../../../components/ButtonV2/ButtonV2'

type LoadingStep = 'connecting' | 'verifying' | 'finishing' | 'success';

const CONNECTING_DELAY = 5000;
const VERIFYING_DELAY = 1500;
const FINISHING_DELAY = 1000;

export default function StripeReturn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [loadingStep, setLoadingStep] = useState<LoadingStep>('connecting');
  const [redirectCountdown, setRedirectCountdown] = useState<number>(3);

  useEffect(() => {
    const completeOnboarding = async () => {
      try {
        // Step 1: Initial connection
        setLoadingStep('connecting');
        
        // Delay to show the connecting step
        await new Promise(resolve => setTimeout(resolve, CONNECTING_DELAY));
        
        // Step 2: Verify account details
        setLoadingStep('verifying');
        
        const response = await PaymentsAPI.completeOnboarding();
        
        // Delay to show the verification step
        await new Promise(resolve => setTimeout(resolve, VERIFYING_DELAY));
        
        // Step 3: Finishing setup
        setLoadingStep('finishing');
        
        if (response.data.success) {
          await new Promise(resolve => setTimeout(resolve, FINISHING_DELAY));
          
          setStatus('success');
          toastService.showToast(t("stripe.return.success.title"), 'success');
          
          // Start countdown for redirect
          const countdownInterval = setInterval(() => {
            setRedirectCountdown(prev => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                navigate('/settings?activeTab=payments');
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          
          return () => clearInterval(countdownInterval);
        } else {
          throw new Error(response.data.message || 'Failed to complete onboarding');
        }
      } catch (error) {
        console.error('Error completing onboarding:', error);
        setStatus('error');
        toastService.showToast(t("stripe.return.error.title"), 'error');
      }
    };

    completeOnboarding();
  }, [navigate, t]);

  const getLoadingMessage = () => {
    switch (loadingStep) {
      case 'connecting':
        return t("stripe.return.steps.connecting");
      case 'verifying':
        return t("stripe.return.steps.verifying");
      case 'finishing':
        return t("stripe.return.steps.finishing");
      default:
        return t("stripe.return.loading");
    }
  };

  return (
    <div className="container mx-auto py-12 max-w-lg">
      <CardV2>
        <CardHeader>
          <CardTitle>{t("stripe.return.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 py-6">
          {status === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-6">
              <div className="w-full max-w-md">
                <div className="flex items-center mb-2">
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center 
                    ${loadingStep === 'connecting' ? 'bg-primary text-white' : 'bg-primary/20 text-primary'}`}>
                    {loadingStep === 'connecting' ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <CheckCircle className="h-5 w-5" />
                    )}
                  </div>
                  <div className="ml-4">
                    <h4 className="font-medium">{t("stripe.return.steps.connecting_title")}</h4>
                    <p className="text-sm text-muted-foreground">{t("stripe.return.steps.connecting_desc")}</p>
                  </div>
                </div>
                
                <div className="flex items-center mb-2">
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center 
                    ${loadingStep === 'verifying' ? 'bg-primary text-white' : 
                      loadingStep === 'finishing' || loadingStep === 'success' ? 'bg-primary/20 text-primary' : 
                      'bg-muted text-muted-foreground'}`}>
                    {loadingStep === 'verifying' ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : loadingStep === 'finishing' || loadingStep === 'success' ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <span className="text-sm">2</span>
                    )}
                  </div>
                  <div className="ml-4">
                    <h4 className="font-medium">{t("stripe.return.steps.verifying_title")}</h4>
                    <p className="text-sm text-muted-foreground">{t("stripe.return.steps.verifying_desc")}</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center 
                    ${loadingStep === 'finishing' ? 'bg-primary text-white' : 
                      loadingStep === 'success' ? 'bg-primary/20 text-primary' : 
                      'bg-muted text-muted-foreground'}`}>
                    {loadingStep === 'finishing' ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : loadingStep === 'success' ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <span className="text-sm">3</span>
                    )}
                  </div>
                  <div className="ml-4">
                    <h4 className="font-medium">{t("stripe.return.steps.finishing_title")}</h4>
                    <p className="text-sm text-muted-foreground">{t("stripe.return.steps.finishing_desc")}</p>
                  </div>
                </div>
              </div>
              
              <p className="text-center font-medium mt-6">
                {getLoadingMessage()}
              </p>
              <p className="text-sm text-muted-foreground text-center">
                {t("stripe.return.please_wait")}
              </p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-md p-6 flex flex-col items-center">
              <CheckCircle className="h-12 w-12 text-green-600 mb-4" />
              <h3 className="text-xl font-medium text-green-900 text-center">
                {t("stripe.return.success.title")}
              </h3>
              <p className="text-green-700 mt-2 text-center">
                {t("stripe.return.success.message")}
              </p>
              <p className="text-sm text-muted-foreground mt-6 text-center">
                {t("stripe.return.redirecting_in", { seconds: redirectCountdown })}
              </p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-md p-6 flex flex-col items-center">
              <AlertCircle className="h-12 w-12 text-red-600 mb-4" />
              <h3 className="text-xl font-medium text-red-900 text-center">
                {t("stripe.return.error.title")}
              </h3>
              <p className="text-red-700 mt-2 text-center">
                {t("stripe.return.error.message")}
              </p>
              <div className="mt-6">
                <ButtonV2 
                  onClick={() => navigate('/settings?activeTab=payments')}
                  variant="outline"
                >
                  {t("stripe.return.back_to_settings")}
                </ButtonV2>
              </div>
            </div>
          )}
        </CardContent>
      </CardV2>
    </div>
  );
} 